import localforage from "localforage";
import Schema from "../type/schema";
import { remove as removeAccents } from "remove-accents-diacritics";

interface Store<T> extends LocalForage {
	getItems(): Promise<T[]>;
	find(query: Query<T>): Promise<T[]>;
	findOne(query: Query<T>): Promise<T>;
	fullTextSearch(query: string): Promise<T[]>;
	clearItems(): Promise<void>;
}

type Query<T> = {
	[P in keyof T]?: T[P];
};

function genGetItems(store: LocalForage) {
	return async () => {
		let items: any[] = [];
		await store.iterate((value) => {
			items.push(value);
		});
		return items;
	};
}

function genFind(store: LocalForage) {
	return async (query: { [key: string]: any }) => {
		let items: any[] = [];
		await store.iterate((value: any) => {
			let isMatch = true;
			for (let key in query) {
				if (query[key] !== value[key]) {
					isMatch = false;
					break;
				}
			}
			if (isMatch) {
				items.push(value);
			}
		});
		return items;
	};
}

function genFindOne(store: LocalForage) {
	return async (query: { [key: string]: any }) => {
		let item: any;
		await store.iterate((value: any) => {
			let isMatch = true;
			for (let key in query) {
				if (query[key] !== value[key]) {
					isMatch = false;
					break;
				}
			}
			if (isMatch) {
				item = value;
				return;
			}
		});
		return item;
	};
}

function genFullTextSearch(store: LocalForage) {
	// TODO: Implement this using fullText field later
	const sanitize = (text: string) => {
		let output;
		output = text.toLowerCase().trim();
		output = removeAccents(output);
		output = output.replace(/\s/g, "");
		return output;
	};

	return async (query: string) => {
		query = sanitize(query);
		let items: any[] = [];

		await store.iterate((item: any) => {
			let isMatch = false;
			for (let key in item) {
				let value = item[key];
				if (typeof value === "object") {
					value = JSON.stringify(value);
				}
				if (typeof value !== "string") {
					value = value.toString();
				}
				value = sanitize(value);
				if (value.includes(query)) {
					isMatch = true;
					break;
				}
			}
			if (isMatch) {
				items.push(item);
			}
		});
		return items;
	};
}

function genClearItems(store: LocalForage) {
	return async () => {
		await store.clear();
	};
}

const ProductStore = localforage.createInstance({
	name: "LiveDb",
	storeName: "Product"
}) as Store<Schema.Product>;

ProductStore.getItems = genGetItems(ProductStore);
ProductStore.find = genFind(ProductStore);
ProductStore.findOne = genFindOne(ProductStore);
ProductStore.fullTextSearch = genFullTextSearch(ProductStore);
ProductStore.clearItems = genClearItems(ProductStore);

const CustomerStore = localforage.createInstance({
	name: "LiveDb",
	storeName: "Customer"
}) as Store<Schema.Customer>;

CustomerStore.getItems = genGetItems(CustomerStore);
CustomerStore.find = genFind(CustomerStore);
CustomerStore.findOne = genFindOne(CustomerStore);
CustomerStore.fullTextSearch = genFullTextSearch(CustomerStore);
CustomerStore.clearItems = genClearItems(CustomerStore);

const ConfigStore = localforage.createInstance({
	name: "LiveDb",
	storeName: "Config"
}) as Store<string>;

export { ProductStore, CustomerStore, ConfigStore };
